import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';

const SearchableSelect = ({
    options = [],
    value,
    onChange,
    placeholder = "Search and select...",
    displayKey = "label",
    valueKey = "value",
    className = "",
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
        option[displayKey].toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get selected option display text
    const selectedOption = options.find(option => option[valueKey] === value);
    const displayText = selectedOption ? selectedOption[displayKey] : '';

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
            }
        };

        // Use both mousedown and touchstart for better mobile support
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(0);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
                break;
            default:
                break;
        }
    };

    const handleSelect = (option) => {
        console.log('SearchableSelect: Selected option', option, 'value:', option[valueKey]);
        onChange(option[valueKey]);
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setSearchTerm('');
    };

    const toggleDropdown = () => {
        if (disabled) return;
        setIsOpen(!isOpen);
        if (!isOpen) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Main Input/Button */}
            <div
                className={`
                    relative w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer touch-manipulation
                    focus-within:ring-2 focus-within:ring-green-500 focus-within:border-green-500
                    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400 active:border-green-400'}
                    ${isOpen ? 'ring-2 ring-green-500 border-green-500' : ''}
                `}
                onClick={toggleDropdown}
                onTouchEnd={(e) => {
                    if (!isOpen) {
                        e.preventDefault();
                        toggleDropdown();
                    }
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1 min-w-0">
                        <Search className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                        {isOpen ? (
                            <input
                                ref={inputRef}
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={placeholder}
                                className="flex-1 outline-none bg-transparent text-sm"
                                disabled={disabled}
                            />
                        ) : (
                            <span className={`flex-1 text-sm truncate ${displayText ? 'text-gray-900' : 'text-gray-500'
                                }`}>
                                {displayText || placeholder}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-1">
                        {value && !disabled && (
                            <button
                                onClick={handleClear}
                                className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        )}
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''
                            }`} />
                    </div>
                </div>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto left-0 right-0">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => (
                            <div
                                key={option[valueKey]}
                                className={`
                                    px-3 py-2 cursor-pointer text-sm touch-manipulation
                                    ${index === highlightedIndex ? 'bg-green-100 text-green-900' : 'hover:bg-gray-100 active:bg-gray-200'}
                                    ${value === option[valueKey] ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-900'}
                                `}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSelect(option);
                                }}
                                onTouchEnd={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSelect(option);
                                }}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                <div className="flex justify-between items-center">
                                    <span className="truncate">{option[displayKey]}</span>
                                    {option.subtitle && (
                                        <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                                            {option.subtitle}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                            {searchTerm ? 'No items found' : 'No options available'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;